import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { Payment } from '../../../api/models';

export interface PaymentConfirmationDialogData {
  payment: Payment;
  newStatus: string;
}

@Component({
  selector: 'app-payment-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDividerModule,
    FormsModule
  ],
  template: `
    <h2 mat-dialog-title class="flex items-center gap-2">
      <mat-icon class="text-blue-500">check_circle</mat-icon>
      <span>Confirm Payment</span>
    </h2>
    
    <mat-dialog-content>
      <div class="space-y-4 py-2">
        <!-- Payment Summary -->
        <div class="bg-gray-50 rounded-lg p-4 space-y-3">
          <h3 class="text-sm font-semibold text-gray-700 uppercase">Payment Summary</h3>
          
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span class="text-gray-600">Payment ID:</span>
              <div class="font-medium">{{ data.payment.transactionId || data.payment.id }}</div>
            </div>
            
            <div>
              <span class="text-gray-600">Order ID:</span>
              <div class="font-medium">{{ data.payment.orderId }}</div>
            </div>
            
            <div>
              <span class="text-gray-600">Payment Method:</span>
              <div class="font-medium capitalize">{{ data.payment.paymentMethod }}</div>
            </div>
            
            <div>
              <span class="text-gray-600">Amount:</span>
              <div class="text-lg font-bold text-green-600">\${{ data.payment.amount.toFixed(2) }}</div>
            </div>
          </div>
        </div>

        <!-- Cash Calculator (only for cash payments) -->
        <div *ngIf="isCashPayment()" class="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <h3 class="text-sm font-semibold text-blue-700 uppercase flex items-center gap-2">
            <mat-icon class="text-sm">calculate</mat-icon>
            Cash Calculator
          </h3>
          
          <mat-form-field class="w-full">
            <mat-label>Cash Received from Customer</mat-label>
            <input 
              matInput 
              type="number" 
              [(ngModel)]="cashReceived"
              (ngModelChange)="calculateChange()"
              placeholder="0.00"
              step="0.01"
              min="0">
            <span matPrefix>\$&nbsp;</span>
          </mat-form-field>

          <div *ngIf="cashReceived !== null && cashReceived >= 0" class="space-y-2">
            <mat-divider></mat-divider>
            
            <div class="flex justify-between items-center">
              <span class="text-gray-600">Amount Due:</span>
              <span class="font-semibold">\${{ data.payment.amount.toFixed(2) }}</span>
            </div>
            
            <div class="flex justify-between items-center">
              <span class="text-gray-600">Cash Received:</span>
              <span class="font-semibold">\${{ cashReceived.toFixed(2) }}</span>
            </div>
            
            <mat-divider></mat-divider>
            
            <div class="flex justify-between items-center p-3 rounded" [class.bg-green-100]="change >= 0" [class.bg-red-100]="change < 0">
              <span class="font-semibold" [class.text-green-700]="change >= 0" [class.text-red-700]="change < 0">
                {{ change >= 0 ? 'Change to Return:' : 'Insufficient Amount:' }}
              </span>
              <span class="text-xl font-bold" [class.text-green-700]="change >= 0" [class.text-red-700]="change < 0">
                \${{ Math.abs(change).toFixed(2) }}
              </span>
            </div>

            <div *ngIf="change < 0" class="text-sm text-red-600 flex items-center gap-1">
              <mat-icon class="text-sm">warning</mat-icon>
              Customer needs to provide \${{ Math.abs(change).toFixed(2) }} more
            </div>
          </div>
        </div>

        <!-- Confirmation Message -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p class="text-sm text-gray-700">
            <mat-icon class="text-yellow-600 text-sm align-middle mr-1">info</mat-icon>
            Are you sure you want to mark this payment as <strong class="uppercase">{{ data.newStatus }}</strong>?
          </p>
        </div>
      </div>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button 
        mat-raised-button 
        color="primary" 
        (click)="onConfirm()"
        [disabled]="!canConfirm()">
        <mat-icon>check</mat-icon>
        Confirm Payment
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class PaymentConfirmationDialogComponent {
  cashReceived: number | null = null;
  change: number = 0;
  Math = Math;

  constructor(
    public dialogRef: MatDialogRef<PaymentConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PaymentConfirmationDialogData
  ) {
    // Pre-fill cash received with exact amount for convenience
    if (this.isCashPayment()) {
      this.cashReceived = this.data.payment.amount;
      this.calculateChange();
    }
  }

  isCashPayment(): boolean {
    return this.data.payment.paymentMethod?.toLowerCase() === 'cash';
  }

  calculateChange(): void {
    if (this.cashReceived !== null && this.cashReceived >= 0) {
      this.change = this.cashReceived - this.data.payment.amount;
    } else {
      this.change = 0;
    }
  }

  canConfirm(): boolean {
    // For cash payments, ensure sufficient cash was received
    if (this.isCashPayment()) {
      return this.cashReceived !== null && this.cashReceived >= this.data.payment.amount;
    }
    // For other payment methods, always allow confirmation
    return true;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}
